import { useRef, useState } from "react";
import { useGesture } from "@use-gesture/react";
import { animated, useSpring } from "@react-spring/web";
import "./taskList.scss";
import { getColor } from "../../display-items/indicator-bars/ProgressBar";
import ClockDurationIcon from "../../display-items/displayIcons/ClockDurationIcon";
import IconButton from "../../control-items/buttons/iconButton/IconButton";
import FinishButton from "../../control-items/buttons/finishTaskButton/FinishButton";
import DeleteButton from "../../control-items/buttons/deleteButton/DeleteButton";
import FinishIcon from "@icons/FinishIcon.svg?react";
import DeleteIcon from "@icons/DeleteIcon.svg?react";

const SWIPE_PX = 100; // distance threshold
const SWIPE_V = 0.5; // velocity threshold

export interface DemoTask {
  id: string;
  title: string;
  urgency: number; // 0-100
  importance: number; // 0-100
  scheduleStart?: Date; // set when the task is scheduled for today
  scheduleEnd?: Date;
}

export const getTaskPriorityValue = (task: DemoTask) =>
  Math.sqrt(task.urgency * task.urgency + task.importance * task.importance) /
  Math.sqrt(2);

interface TaskItemDemoProps {
  task: DemoTask;
  onFinish: (id: string) => void;
  onDelete: (id: string) => void;
  showSchedule?: boolean;
}

function TaskItemDemo({
  task,
  onFinish,
  onDelete,
  showSchedule = false,
}: TaskItemDemoProps) {
  const itemRef = useRef<HTMLDivElement | null>(null);
  const [backgroundStyle, setBackgroundStyle] = useState<string | null>(null);

  const handleDeleteTask = () => onDelete(task.id);
  const handleFinishTask = () => onFinish(task.id);

  // Swipe Handling
  const [{ x }, api] = useSpring(() => ({ x: 0 }));

  const bind = useGesture(
    {
      onDrag: ({ last, down, movement: [mx], velocity: [vx], event }) => {
        // Block drag on inputs/buttons
        if (
          (event.target as HTMLElement)?.closest(
            "input,textarea,button,[contenteditable]",
          )
        ) {
          return;
        }

        // Update spring while dragging
        api.start({ x: down ? mx : 0, immediate: down });
        const passedDistance = Math.abs(mx) > SWIPE_PX;
        const passedVelocity = Math.abs(vx) > SWIPE_V;

        if (passedDistance || passedVelocity) {
          if (mx > 0) {
            // swipe right -> delete
            setBackgroundStyle("bg_red300");
          } else {
            // swipe left -> finish
            setBackgroundStyle("bg_green300");
          }
        } else {
          setBackgroundStyle(null);
        }

        // If gesture ends, decide action
        if (last) {
          if (passedDistance || passedVelocity) {
            if (mx > 0) {
              handleDeleteTask();
            } else {
              handleFinishTask();
            }
          }

          // snap back after handling
          api.start({ x: 0 });
          setBackgroundStyle(null);
        }
      },
    },
    {
      drag: {
        axis: "x",
        filterTaps: false,
        from: () => [x.get(), 0],
        bounds: { left: -200, right: 200 },
        rubberband: 0.2,
        preventScroll: true, // stops vertical page scrolling while dragging
      },
    },
  );

  return (
    <div
      className="task-list-item__element"
      style={{ borderLeft: "none" }}
      ref={itemRef}
    >
      {backgroundStyle === "bg_green300" && <FinishButton onClick={undefined} />}

      <animated.div
        {...bind()}
        className="flex task-list-item__inner"
        style={{
          transform: x.to((v) => `translate3d(${v}px,0,0)`),
          borderLeft: `5px solid ${getColor(getTaskPriorityValue(task), true)}`,
        }}
      >
        <div className="task-list-item__container">
          <div
            className="task-list-item__wrapper"
            style={{ cursor: "default" }}
          >
            <div className="task-list-item__content">
              <h3 className="task-list-item__heading h3">{task.title}</h3>
            </div>
          </div>
          <div className="container" style={{ gap: "10px", flexShrink: 0 }}>
            {showSchedule && task.scheduleStart && task.scheduleEnd && (
              <ClockDurationIcon
                minutes={
                  (task.scheduleEnd.getTime() - task.scheduleStart.getTime()) /
                  60000
                }
                color="blue"
              />
            )}
            <IconButton
              onClick={() => onFinish(task.id)}
              icon={FinishIcon}
              buttonClasses="bg_green300"
              css="fill_green_800 icon__mid"
            />
            <IconButton
              onClick={() => onDelete(task.id)}
              icon={DeleteIcon}
              buttonClasses="bg_red300"
              css="fill_red_800 icon__mid"
            />
          </div>
        </div>
      </animated.div>

      {backgroundStyle === "bg_red300" && <DeleteButton onClick={undefined} />}
    </div>
  );
}

export default TaskItemDemo;
